import { useCallback, useState } from 'react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { api, queryKeys } from '@/lib/api'
import { useNotificationActions } from '@/lib/notifications'
import type { AgentContactRecord } from '@/lib/agent-contact-utils'
import { toDisplayString } from '../utils/display'
import type { ContactFormData } from '@/components/ui/unified-contact-form'
import { normalizeWhatsAppPhone } from '@/lib/phone'

export type ContactFormMode = 'create' | 'edit'

export interface ContactFormValues {
  firstName: string
  lastName: string
  title: string
  email: string
  phoneNumber: string
  fbMessengerId: string
  telegramId: string
  telegramUserId: string
  instagramId: string
  weChatId: string
  lineId: string
}

const createEmptyContactFormValues = (): ContactFormValues => ({
  firstName: '',
  lastName: '',
  title: '',
  email: '',
  phoneNumber: '',
  fbMessengerId: '',
  telegramId: '',
  telegramUserId: '',
  instagramId: '',
  weChatId: '',
  lineId: '',
})

const sanitizeContactFormValue = (value: string): string | undefined => {
  const trimmed = value?.trim() ?? ''
  return trimmed.length > 0 ? trimmed : undefined
}

const splitName = (
  value: string | null | undefined
): { firstName: string; lastName: string } => {
  if (!value) {
    return { firstName: '', lastName: '' }
  }

  const trimmed = value.trim()
  if (!trimmed) {
    return { firstName: '', lastName: '' }
  }

  const parts = trimmed.split(/\s+/)
  if (parts.length === 1) {
    return { firstName: parts[0] ?? '', lastName: '' }
  }

  return {
    firstName: parts[0] ?? '',
    lastName: parts.slice(1).join(' '),
  }
}

const buildContactPayload = (values: ContactFormValues) => ({
  firstName: values.firstName.trim(),
  lastName: sanitizeContactFormValue(values.lastName),
  title: sanitizeContactFormValue(values.title),
  email: sanitizeContactFormValue(values.email),
  phoneNumber: sanitizeContactFormValue(
    normalizeWhatsAppPhone(values.phoneNumber)
  ),
  fbMessengerId: sanitizeContactFormValue(values.fbMessengerId),
  telegramId: sanitizeContactFormValue(values.telegramId),
  telegramUserId: sanitizeContactFormValue(values.telegramUserId),
  instagramId: sanitizeContactFormValue(values.instagramId),
  weChatId: sanitizeContactFormValue(values.weChatId),
  lineId: sanitizeContactFormValue(values.lineId),
})

const normalizeSubmittedValues = (
  data: ContactFormData
): ContactFormValues => ({
  firstName: data.firstName ?? '',
  lastName: data.lastName ?? '',
  title: data.title ?? '',
  email: data.email ?? '',
  phoneNumber: data.phoneNumber ?? '',
  fbMessengerId: data.fbMessengerId ?? '',
  telegramId: data.telegramId ?? '',
  telegramUserId: data.telegramUserId ?? '',
  instagramId: data.instagramId ?? '',
  weChatId: data.weChatId ?? '',
  lineId: data.lineId ?? '',
})

export interface ContactFormController {
  isContactDialogOpen: boolean
  contactFormMode: ContactFormMode
  contactFormValues: ContactFormValues
  contactFormErrors: Partial<Record<keyof ContactFormValues, string>>
  isContactSubmitting: boolean
  contactMutationErrorMessage: string | null
  handleContactDialogOpenChange: (open: boolean) => void
  openCreateContactDialog: () => void
  openEditContactDialog: () => void
  handleContactFieldChange: (
    field: keyof ContactFormValues,
    value: string
  ) => void
  handleContactFormSubmit: (values: ContactFormData) => Promise<void>
}

interface UseContactFormOptions {
  sessionContactDefaults: ContactFormValues
  contactMatch?: AgentContactRecord
  resolvedContactId: number | null
}

export function useContactForm({
  sessionContactDefaults,
  contactMatch,
  resolvedContactId,
}: UseContactFormOptions): ContactFormController {
  const notificationActions = useNotificationActions()
  const queryClient = useQueryClient()
  const [isContactDialogOpen, setIsContactDialogOpen] = useState(false)
  const [contactFormMode, setContactFormMode] =
    useState<ContactFormMode>('create')
  const [contactFormValues, setContactFormValues] = useState<ContactFormValues>(
    createEmptyContactFormValues()
  )
  const [contactFormErrors, setContactFormErrors] = useState<
    Partial<Record<keyof ContactFormValues, string>>
  >({})

  const resetContactDialog = useCallback(() => {
    setContactFormErrors({})
    setContactFormValues(createEmptyContactFormValues())
    setContactFormMode('create')
  }, [])

  const invalidateContactQueries = useCallback(async () => {
    await queryClient.invalidateQueries({
      queryKey: queryKeys.agentContacts.all,
      exact: false,
    })
  }, [queryClient])

  const createContactMutation = useMutation<
    AgentContactRecord,
    Error,
    ContactFormValues
  >({
    mutationFn: async (values: ContactFormValues) => {
      const payload = buildContactPayload(values)
      const response = await api.sessions.createContact(payload)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to create contact')
      }
      return response.data as AgentContactRecord
    },
    onSuccess: async () => {
      await invalidateContactQueries()
      notificationActions.showSuccess(
        'Contact created',
        'Contact saved successfully.'
      )
      resetContactDialog()
      setIsContactDialogOpen(false)
    },
    onError: error => {
      console.error('Failed to create contact:', error)
      notificationActions.showError(
        'Create contact failed',
        error instanceof Error
          ? error.message
          : 'Unable to create contact. Please try again.'
      )
    },
  })

  const updateContactMutation = useMutation<
    AgentContactRecord,
    Error,
    { id: number; values: ContactFormValues }
  >({
    mutationFn: async ({
      id,
      values,
    }: {
      id: number
      values: ContactFormValues
    }) => {
      const payload = buildContactPayload(values)
      const response = await api.sessions.updateContact(id, payload)
      if (!response.success || !response.data) {
        throw new Error(response.error || 'Failed to update contact')
      }
      return response.data as AgentContactRecord
    },
    onSuccess: async () => {
      await invalidateContactQueries()
      notificationActions.showSuccess(
        'Contact updated',
        'Contact details have been saved.'
      )
      resetContactDialog()
      setIsContactDialogOpen(false)
    },
    onError: error => {
      console.error('Failed to update contact:', error)
      notificationActions.showError(
        'Update contact failed',
        error instanceof Error
          ? error.message
          : 'Unable to update contact. Please try again.'
      )
    },
  })

  const isContactSubmitting =
    createContactMutation.isPending || updateContactMutation.isPending

  const contactMutationErrorMessage =
    createContactMutation.error?.message ??
    updateContactMutation.error?.message ??
    null

  const handleContactDialogOpenChange = useCallback(
    (open: boolean) => {
      setIsContactDialogOpen(open)
      if (!open) {
        resetContactDialog()
        createContactMutation.reset()
        updateContactMutation.reset()
      }
    },
    [createContactMutation, resetContactDialog, updateContactMutation]
  )

  const openCreateContactDialog = useCallback(() => {
    setContactFormMode('create')
    setContactFormErrors({})
    setContactFormValues({ ...sessionContactDefaults })
    setIsContactDialogOpen(true)
  }, [sessionContactDefaults])

  const openEditContactDialog = useCallback(() => {
    if (!contactMatch) {
      return
    }

    const fallbackFullName =
      toDisplayString(contactMatch.fullName) ??
      toDisplayString(contactMatch.name) ??
      toDisplayString(contactMatch.title) ??
      ''
    const derivedName = splitName(fallbackFullName)

    setContactFormMode('edit')
    setContactFormErrors({})
    setContactFormValues({
      firstName:
        toDisplayString(contactMatch.firstName) ?? derivedName.firstName,
      lastName: toDisplayString(contactMatch.lastName) ?? derivedName.lastName,
      title: toDisplayString(contactMatch.title) ?? '',
      email: toDisplayString(contactMatch.email) ?? '',
      phoneNumber:
        toDisplayString(contactMatch.phoneNumber) ??
        toDisplayString(contactMatch.waId) ??
        '',
      fbMessengerId: toDisplayString(contactMatch.fbMessengerId) ?? '',
      telegramId: toDisplayString(contactMatch.telegramId) ?? '',
      telegramUserId: toDisplayString(contactMatch.telegramUserId) ?? '',
      instagramId: toDisplayString(contactMatch.instagramId) ?? '',
      weChatId: toDisplayString(contactMatch.weChatId) ?? '',
      lineId: toDisplayString(contactMatch.lineId) ?? '',
    })
    setIsContactDialogOpen(true)
  }, [contactMatch])

  const handleContactFieldChange = useCallback(
    (field: keyof ContactFormValues, value: string) => {
      const trimmedValue = value
      setContactFormValues(prev => ({
        ...prev,
        [field]: trimmedValue,
      }))
      setContactFormErrors(prev => {
        if (!prev[field] && !prev.phoneNumber) {
          return prev
        }
        const next = { ...prev }
        if (next[field]) {
          delete next[field]
        }
        if (field !== 'phoneNumber' && trimmedValue.trim().length > 0) {
          delete next.phoneNumber
        }
        return next
      })
    },
    []
  )

  const validateContactForm = useCallback((values: ContactFormValues) => {
    const errors: Partial<Record<keyof ContactFormValues, string>> = {}
    const trimmedFirstName = values.firstName.trim()
    if (!trimmedFirstName) {
      errors.firstName = 'First name is required.'
    }

    const trimmedEmail = values.email.trim()
    if (
      trimmedEmail &&
      !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail.toLowerCase())
    ) {
      errors.email = 'Enter a valid email address.'
    }

    const trimmedPhone = values.phoneNumber?.trim()
    const normalizedPhone = normalizeWhatsAppPhone(values.phoneNumber)
    const trimmedMessenger = values.fbMessengerId?.trim()
    const trimmedTelegram = values.telegramId?.trim()
    const trimmedTelegramUserId = values.telegramUserId?.trim()
    const trimmedInstagram = values.instagramId?.trim()
    const trimmedWeChat = values.weChatId?.trim()
    const trimmedLine = values.lineId?.trim()

    const hasContactMethod =
      Boolean(trimmedEmail) ||
      Boolean(normalizedPhone) ||
      Boolean(trimmedMessenger) ||
      Boolean(trimmedTelegram) ||
      Boolean(trimmedTelegramUserId) ||
      Boolean(trimmedInstagram) ||
      Boolean(trimmedWeChat) ||
      Boolean(trimmedLine)

    if (!hasContactMethod) {
      errors.phoneNumber = 'Provide at least one contact method.'
    } else if (trimmedPhone && !normalizedPhone) {
      errors.phoneNumber = 'Enter a valid phone number.'
    }

    setContactFormErrors(errors)
    return Object.keys(errors).length === 0
  }, [])

  const handleContactFormSubmit = useCallback(
    async (submittedData: ContactFormData) => {
      const normalizedValues = normalizeSubmittedValues(submittedData)

      setContactFormValues(normalizedValues)

      if (!validateContactForm(normalizedValues)) {
        return
      }

      try {
        if (contactFormMode === 'edit') {
          if (!resolvedContactId) {
            notificationActions.showError(
              'Update failed',
              'This contact does not have a valid identifier.'
            )
            return
          }
          await updateContactMutation.mutateAsync({
            id: resolvedContactId,
            values: normalizedValues,
          })
          return
        }

        await createContactMutation.mutateAsync(normalizedValues)
      } catch (error) {
        console.error('Contact form submission failed:', error)
        notificationActions.showError(
          'Unable to save contact',
          'Something went wrong while saving the contact. Please try again.'
        )
      }
    },
    [
      contactFormMode,
      createContactMutation,
      notificationActions,
      resolvedContactId,
      updateContactMutation,
      validateContactForm,
    ]
  )

  return {
    isContactDialogOpen,
    contactFormMode,
    contactFormValues,
    contactFormErrors,
    isContactSubmitting,
    contactMutationErrorMessage,
    handleContactDialogOpenChange,
    openCreateContactDialog,
    openEditContactDialog,
    handleContactFieldChange,
    handleContactFormSubmit,
  }
}
